import { useState, useEffect, useRef, useCallback, useLayoutEffect, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { debounce } from 'lodash';
import { ArrowRight } from 'lucide-react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination, Autoplay } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import Skeleton from '../../../components/ui/skeleton';
import Heading from '../../../components/Heading';
import TourCard from './TourCard';
import useTourPackage from '../../../stores/useTourPackage';
import { LanguageContext } from '../../../contexts/LanguageContext';
import Link from '../../../components/ui/Link';
import { Button } from '../../../components/ui/button';
import { DETAILSERVICE_PAGE_NOID } from '../../../routes/routeConstant';

export function TourSlider({ className = '' }) {
  const { t } = useTranslation();
  const { language } =
    useContext(LanguageContext) ||
    (() => {
      throw new Error('LanguageSwitcher must be used within a LanguageProvider');
    })();
  const lang = language === 'id';
  const { tours, fetchLoading, hasFetched, fetchTours, page, hasMore, isLoadingNextPage } = useTourPackage();
  const [slidesPerView, setSlidesPerView] = useState(1.5);
  const [cardWidth, setCardWidth] = useState(200);
  const containerRef = useRef<HTMLDivElement>(null);
  const swiperRef = useRef<any>(null);
  const { id } = useParams();
  const [visibleTour, setVisibleTour] = useState(tours);
  const [isInitialRender, setIsInitialRender] = useState(true);
  const SERVICE_CARD_GAP = 8;

  const updateLayout = useCallback(() => {
    const width = window.innerWidth;
    let newCardWidth = 250;
    if (width < 370) newCardWidth = 158;
    else if (width < 640) newCardWidth = 150;
    else if (width < 1024) newCardWidth = 180;

    setCardWidth(newCardWidth);

    if (containerRef.current) {
      const containerWidth = containerRef.current.offsetWidth;
      const calculatedSlides = containerWidth / (newCardWidth + SERVICE_CARD_GAP);
      let slidesToSet;

      if (width < 300) slidesToSet = 1.6;
      else if (width < 400) slidesToSet = 2;
      else if (width < 620) slidesToSet = 2.8;
      else if (width < 700) slidesToSet = 3;
      else if (width < 980) slidesToSet = 3.5;
      else if (width > 980) slidesToSet = 5;
      else slidesToSet = Math.floor(calculatedSlides);

      setSlidesPerView(slidesToSet);
    }
  }, []);

  useLayoutEffect(() => {
    updateLayout();
  }, [updateLayout, tours]);

  useEffect(() => {
    updateLayout();
    window.addEventListener('resize', updateLayout);
    return () => window.removeEventListener('resize', updateLayout);
  }, [updateLayout]);

  useEffect(() => {
    if (!hasFetched) fetchTours(1, true);
  }, [fetchTours, hasFetched]);

  useEffect(() => {
    if (tours.length > 0 && id !== undefined) {
      setVisibleTour(tours);
    }
    if (tours.length > 0 && id === undefined) {
      setVisibleTour(tours.slice(0, 7));
    }
    setIsInitialRender(false);
  }, [tours, id]);

  useEffect(() => {
    if (swiperRef.current?.swiper) {
      swiperRef.current.swiper.update();
    }
  }, [visibleTour, slidesPerView]);

  const handleReachEndInner = useCallback(() => {
    if (isInitialRender) return;
    if (hasMore && !isLoadingNextPage && visibleTour.length >= slidesPerView) {
      fetchTours(page + 1, false);
    }
  }, [fetchTours, page, hasMore, isLoadingNextPage, visibleTour.length, slidesPerView, isInitialRender]);

  const handleReachEnd = debounce(handleReachEndInner, 200);

  const skeletonSlides = Array.from({ length: 10 }, (_, i) => (
    <SwiperSlide key={`skeleton-${i}`} className="pb-12" style={{ width: cardWidth }}>
      <Skeleton className="shadow-md sm:h-72 h-64 w-full" />
    </SwiperSlide>
  ));

  const tourSlides = visibleTour?.map((tour) => (
    <SwiperSlide key={tour.tour_id} className="pb-12" style={{ width: cardWidth }}>
      <TourCard
        name={lang ? tour.destination_tour : tour.destination_tour_en || tour.destination_tour}
        duration={tour.duration_tour}
        price={tour.price_tour}
        imageUrl={tour.image_tour[0] || '/assets/no-data-image.svg'}
        className="w-full"
        TourId={tour.tour_id}
      />
    </SwiperSlide>
  ));

  return (
    <section
      ref={containerRef}
      className={
        tours.length === 0
          ? 'hidden'
          : `${className} relative flex flex-col sm:text-md items-center gap-4 overflow-hidden mt-4`
      }
    >
      <div className="flex flex-col gap-1 text-center mb-4 sm:text-base text-sm ">
        <Heading>{t('tagline.detail-service.tour.title')}</Heading>
        <p>{t('tagline.detail-service.tour.tagline')}</p>
        <Link to={DETAILSERVICE_PAGE_NOID} className={id === undefined ? 'block' : 'hidden'}>
          <Button variant="link" className="text-secondary">
            {t('footer.services.see_more')}
            <ArrowRight />
          </Button>
        </Link>
      </div>
      <div className="relative w-[90vw] max-w-screen-xl">
        {id === undefined ? (
          <Swiper
            modules={[Pagination, Autoplay]}
            autoplay={{ delay: 2500, disableOnInteraction: false, pauseOnMouseEnter: true }}
            pagination={
              tours.length >= slidesPerView ? { clickable: true, dynamicBullets: true, dynamicMainBullets: 3 } : false
            }
            loop={tours.length >= slidesPerView}
            centerInsufficientSlides
            slidesPerView={slidesPerView}
            spaceBetween={SERVICE_CARD_GAP}
          >
            {fetchLoading && tours.length === 0 ? skeletonSlides : tourSlides}
          </Swiper>
        ) : (
          <Swiper
            modules={[Pagination, Autoplay]}
            onReachEnd={handleReachEnd}
            pagination={{ clickable: true, dynamicBullets: true, dynamicMainBullets: 3 }}
            centerInsufficientSlides
            slidesPerView={slidesPerView}
            spaceBetween={SERVICE_CARD_GAP}
          >
            {fetchLoading && tours.length === 0
              ? skeletonSlides
              : [
                  ...tourSlides,
                  ...(isLoadingNextPage
                    ? [
                        <SwiperSlide key="loading" style={{ width: cardWidth }}>
                          <Skeleton className={`shadow-md sm:h-72 h-64  w-[${cardWidth}px]`} />
                        </SwiperSlide>,
                      ]
                    : []),
                ]}
          </Swiper>
        )}
      </div>
    </section>
  );
}
